/**
 * Q4 acquisition offers — runtime.
 *
 * snippets/acq-offer-head.liquid decides the visitor's offer before paint and
 * exposes it as window.wlOffer (e.g. "gift", or null). This file:
 *   - saves the offer on the cart as the _offer attribute, so it reaches the
 *     order (for reporting; the checkout discount can check the gift);
 *   - runs the gift picker (snippets/acq-gift-picker.liquid): opens on add
 *     to cart when the cart has something paid and no gift yet, adds the
 *     chosen pair with the _offer_gift property;
 *   - keeps the gift honest: one gift, quantity 1, and only while the cart has
 *     something paid in it and the visitor has the gift offer.
 *
 * Loaded only while an offer is enabled in Theme settings > Acquisition offers.
 */

const GIFT_PROPERTY = "_offer_gift";
const OFFER_ATTRIBUTE = "_offer";
// Lines that don't count as a purchase for the gift
const NON_QUALIFYING_HANDLES = ["priority-processing"];

const offer = window.wlOffer || null;
const routes = window.routes || {};
const cartUrl = routes.cartUrl || "/cart";

function track(action, detail = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "acq_offer", offer, action, ...detail });
}

function postJSON(url, body) {
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  }).then((response) =>
    response.json().then((json) => {
      if (!response.ok) throw json;
      return json;
    }),
  );
}

function getCart() {
  return fetch(`${cartUrl}.js`, { headers: { Accept: "application/json" } }).then(
    (response) => response.json(),
  );
}

// The cart section listens for cart:refresh on <body> (domDelegate rooted at
// document.body), so it has to be fired there: an event fired on document
// never reaches it. On /cart the drawer's re-render fails (it looks for the
// main cart's markup), so that page reloads instead, as the theme does there.
function refreshCart() {
  if (window.theme && window.theme.pageType === "cart") {
    window.location.reload();
    return;
  }
  document.body.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
}

const isGiftLine = (item) => Boolean(item.properties && item.properties[GIFT_PROPERTY]);
const isQualifyingLine = (item) =>
  !isGiftLine(item) && !NON_QUALIFYING_HANDLES.includes(item.handle);

/* ---------------------------------------------------------------------------
 * Cart: offer attribute and gift rules
 * ------------------------------------------------------------------------- */

let syncing = false;
let syncQueued = false;

async function syncCart(cart) {
  // A cart change during a sync is re-checked once the sync finishes
  if (syncing) {
    syncQueued = true;
    return;
  }
  syncing = true;
  try {
    cart = cart || (await getCart());
    let changed = false;

    const attribute = (cart.attributes && cart.attributes[OFFER_ATTRIBUTE]) || null;
    if (attribute !== offer) {
      await postJSON(`${cartUrl}/update.js`, {
        attributes: { [OFFER_ATTRIBUTE]: offer || "" },
      });
    }

    // One gift, quantity 1, only with a paid item and the gift offer
    const gifts = cart.items.filter(isGiftLine);
    if (gifts.length) {
      const keepGift = offer === "gift" && cart.items.some(isQualifyingLine);
      const updates = {};
      gifts.forEach((item, index) => {
        const quantity = keepGift && index === 0 ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
      if (Object.keys(updates).length) {
        await postJSON(`${cartUrl}/update.js`, { updates });
        changed = true;
        if (!keepGift) track("gift_removed");
      }
    }

    if (changed) refreshCart();
  } catch (error) {
    console.warn("[acq-offers] cart sync failed", error);
  } finally {
    syncing = false;
    if (syncQueued) {
      syncQueued = false;
      syncCart();
    }
  }
}

/* ---------------------------------------------------------------------------
 * Gift picker
 * ------------------------------------------------------------------------- */

// Shown as "Size: Medium"; other values (e.g. M-Wide) show as they are
const SIZE_NAMES = { S: "Small", M: "Medium", L: "Large", XL: "Extra Large" };
const isWideSize = (size) => /wide/i.test(size);

class GiftPicker {
  constructor(dialog) {
    this.dialog = dialog;
    this.products = JSON.parse(
      dialog.querySelector("[data-acq-gift-products]").textContent,
    );
    this.addButton = dialog.querySelector("[data-acq-gift-add]");
    this.error = dialog.querySelector("[data-acq-gift-error]");
    this.mainImage = dialog.querySelector("[data-acq-gift-main-image]");
    this.thumbs = dialog.querySelector("[data-acq-gift-thumbs]");
    this.sizes = dialog.querySelector("[data-acq-gift-sizes]");
    this.existingGiftKey = null;
    this.size = null;

    dialog.addEventListener("change", (event) => {
      const { name, value } = event.target;
      if (name === "acq-gift-color") this.selectProduct(Number(value));
      if (name === "acq-gift-size") this.selectSize(value);
    });
    dialog.addEventListener("click", (event) => {
      const thumb = event.target.closest("[data-acq-gift-thumb]");
      if (thumb) {
        this.showImage(thumb.dataset.acqGiftThumb);
        this.setActiveThumb(thumb);
      }
      // Click on the backdrop closes the dialog
      if (event.target === dialog) dialog.close();
    });
    this.addButton.addEventListener("click", () => this.add());
  }

  setText(selector, text) {
    this.dialog.querySelectorAll(selector).forEach((element) => {
      element.textContent = text;
    });
  }

  // Swaps everything to one product (one color); keeps the size if it can
  selectProduct(index, preferredSize = this.size) {
    const product = this.products[index] || this.products[0];
    this.product = product;
    const radio = this.dialog.querySelector(`[name="acq-gift-color"][value="${index}"]`);
    if (radio) radio.checked = true;

    this.setText("[data-acq-gift-color-name]", product.color);
    this.setText("[data-acq-gift-compare], [data-acq-gift-compare-sr]", product.price);

    const rating = this.dialog.querySelector("[data-acq-gift-rating]");
    rating.hidden = !product.reviews;
    this.setText("[data-acq-gift-review-count]", `${product.reviews} Reviews`);

    this.renderImages();
    this.renderSizes(preferredSize);
  }

  renderImages() {
    const images = this.product.images;
    if (images[0]) this.showImage(images[0].src);
    this.thumbs.innerHTML = images.length > 1
      ? images
          .map(
            (image, index) => `
              <li>
                <button type="button" class="AcqGiftPicker__Thumb${index === 0 ? " is-active" : ""}"
                  data-acq-gift-thumb="${image.src}" aria-label="Show image ${index + 1}">
                  <img src="${image.thumb}" alt="" loading="lazy" width="150" height="150">
                </button>
              </li>`,
          )
          .join("")
      : "";
  }

  // Regular sizes on one row, wide sizes on the next, as in the design
  renderSizes(preferredSize) {
    const variants = this.product.variants;
    const pill = (variant) => {
      const id = `AcqGiftSize-${variant.id}`;
      return `
        <input type="radio" class="u-visually-hidden" id="${id}" name="acq-gift-size"
          value="${variant.size}"${variant.available ? "" : " disabled"}>
        <label for="${id}" class="AcqGiftPicker__Pill${variant.available ? "" : " is-unavailable"}">${variant.size}</label>`;
    };
    const regular = variants.filter((variant) => !isWideSize(variant.size));
    const wide = variants.filter((variant) => isWideSize(variant.size));
    this.sizes.innerHTML = [regular, wide]
      .filter((row) => row.length)
      .map((row) => `<div class="AcqGiftPicker__SizeRow">${row.map(pill).join("")}</div>`)
      .join("");

    const start =
      variants.find((variant) => variant.size === preferredSize && variant.available) ||
      variants.find((variant) => variant.available) ||
      variants[0];
    this.selectSize(start.size);
  }

  selectSize(size) {
    this.size = size;
    this.variant = this.product.variants.find((variant) => variant.size === size);
    const input = this.sizes.querySelector(`[name="acq-gift-size"][value="${size}"]`);
    if (input) input.checked = true;
    this.setText("[data-acq-gift-size-name]", SIZE_NAMES[size] || size);
    this.addButton.disabled = !(this.variant && this.variant.available);
  }

  setActiveThumb(thumb) {
    this.dialog.querySelectorAll("[data-acq-gift-thumb]").forEach((item) => {
      item.classList.toggle("is-active", item === thumb);
    });
  }

  showImage(src) {
    if (!this.mainImage) return;
    this.mainImage.removeAttribute("srcset");
    this.mainImage.src = src;
  }

  // cart: the cart if the caller has just fetched it (saves a round trip)
  async open(source, cart = null) {
    if (this.dialog.open) return;
    this.error.hidden = true;
    if (!cart) {
      try {
        cart = await getCart();
      } catch (error) {
        // Open with the defaults
      }
    }
    const gift = cart && cart.items.find(isGiftLine);
    this.existingGiftKey = gift ? gift.key : null;
    // The add-to-cart open never shows while a gift is already in the cart
    if (gift && source === "add_to_cart") return;

    // Reopening for an existing gift starts on its color and size
    let index = 0;
    let size = this.size;
    if (gift) {
      this.products.forEach((product, productIndex) => {
        const variant = product.variants.find((item) => item.id === gift.variant_id);
        if (variant) {
          index = productIndex;
          size = variant.size;
        }
      });
    } else if (this.product) {
      index = this.products.indexOf(this.product);
    }
    this.selectProduct(index, size);

    if (!this.dialog.open) this.dialog.showModal();
    track("gift_picker_open", { source });
  }

  async add() {
    if (!this.variant || !this.variant.available) return;
    this.addButton.disabled = true;
    this.error.hidden = true;
    try {
      const replacing = Boolean(this.existingGiftKey);
      if (replacing) {
        await postJSON(`${cartUrl}/change.js`, { id: this.existingGiftKey, quantity: 0 });
      }
      const response = await postJSON(routes.cartAddUrl ? `${routes.cartAddUrl}.js` : "/cart/add.js", {
        items: [{ id: this.variant.id, quantity: 1, properties: { [GIFT_PROPERTY]: "1" } }],
      });
      this.dialog.close();
      track(replacing ? "gift_changed" : "gift_added", { variant_id: this.variant.id });
      // Re-renders and opens the cart drawer. acqGift stops the picker from
      // reopening itself; quantity 0 when replacing keeps the item count right.
      document.dispatchEvent(
        new CustomEvent("product:added", {
          bubbles: true,
          detail: {
            product: response.items ? response.items[0] : response,
            variant: this.variant,
            quantity: replacing ? 0 : 1,
            acqGift: true,
          },
        }),
      );
    } catch (error) {
      this.error.textContent =
        (error && (error.description || error.message)) ||
        "Sorry, we couldn't add your free pair. Please try again.";
      this.error.hidden = false;
      this.addButton.disabled = false;
    }
  }
}

function initGift() {
  const dialog = document.getElementById("AcqGiftPicker");
  if (!dialog) return;
  const picker = new GiftPicker(dialog);

  document.addEventListener("click", (event) => {
    const opener = event.target.closest("[data-acq-gift-open]");
    if (!opener) return;
    event.preventDefault();
    picker.open(opener.closest(".CartItem") ? "cart_line" : "cart_reminder");
  });

  // Opens on add to cart only, when the cart then has something paid and no
  // gift. One add can fire product:added more than once, hence checking.
  let checking = false;
  document.addEventListener("product:added", async (event) => {
    if ((event.detail && event.detail.acqGift) || checking || dialog.open) return;
    checking = true;
    try {
      const cart = await getCart();
      if (!cart.items.some(isGiftLine) && cart.items.some(isQualifyingLine)) {
        await picker.open("add_to_cart", cart);
      }
    } catch (error) {
      console.warn("[acq-offers] gift picker check failed", error);
    } finally {
      checking = false;
    }
  });
}

/* ---------------------------------------------------------------------------
 * Init
 * ------------------------------------------------------------------------- */

if (offer === "gift") initGift();

syncCart();
// Any cart change (quantity, remove, add) re-checks the gift rules
document.addEventListener("cart:replaceContent", () => syncCart());
